import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mail } from "lucide-react";
import logo from "@/assets/logo_header.png";
import logoFooter from "@/assets/logo_w_footer.png";

const PoliticaPrivacidade = () => {
  const navigate = useNavigate();

  const scrollToSection = (id: string) => {
    navigate("/");
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header fixo */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b z-50 shadow-sm">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <img
            src={logo}
            alt="Elinia"
            className="cursor-pointer"
            onClick={() => {
              navigate("/");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }}
          />

          <nav className="hidden md:flex items-center gap-6 text-sm">
            <button onClick={() => {
              navigate("/sobre");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }} className="hover:text-primary transition-colors">Sobre</button>
            <button onClick={() => {
              navigate("/como-funciona");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }} className="hover:text-primary transition-colors">Como funciona</button>
            <button onClick={() => {
              navigate("/precos");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }} className="hover:text-primary transition-colors">Preços</button>
            <button onClick={() => scrollToSection("cadastre-se")} className="hover:text-primary transition-colors">Cadastre-se</button>
            <button onClick={() => {
              navigate("/cases");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }} className="hover:text-primary transition-colors">Cases</button>
            <button onClick={() => {
              navigate("/blog");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }} className="hover:text-primary transition-colors">Blog</button>
            <button onClick={() => {
              navigate("/contato");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }} className="hover:text-primary transition-colors">Contato</button>
          </nav>

          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate("/auth")}>
              Entrar
            </Button>
            <Button onClick={() => {
              navigate("/onboarding");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }} className="shadow-soft">
              Comece grátis
            </Button>
          </div>
        </div>
      </header>

      {/* Conteúdo */}
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <Button variant="ghost" onClick={() => navigate("/")} className="mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>

          <article className="prose prose-lg max-w-none space-y-8">
            <h1 className="text-4xl font-bold mb-8 text-primary">Política de Privacidade</h1>

            <section>
              <h2 className="text-2xl font-bold mb-4">1. Introdução</h2>
              <p>A Elinia valoriza a segurança e a privacidade de todos os usuários. Esta Política de Privacidade explica como coletamos, usamos, armazenamos e protegemos os dados pessoais e clínicos compartilhados pelos profissionais de saúde e seus pacientes na plataforma.</p>
              <p>Ao utilizar a Elinia, você concorda com os termos descritos abaixo.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. Quem somos</h2>
              <p>A Elinia é uma plataforma de tecnologia em saúde que utiliza Inteligência Artificial para ajudar profissionais da área médica e correlata a organizar, analisar e interpretar dados clínicos de forma mais eficiente e segura.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. Dados coletados</h2>
              <p>Podemos coletar os seguintes tipos de dados:</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">3.1. Dados do profissional de saúde</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Nome, CPF/CNPJ, e-mail, telefone e especialidade;</li>
                <li>Informações de acesso (login e senha);</li>
                <li>Dados de uso e interação com a plataforma.</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">3.2. Dados dos pacientes (inseridos pelo profissional)</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Dados de identificação e contato, quando fornecidos;</li>
                <li>Exames laboratoriais, imagens, relatórios e documentos clínicos;</li>
                <li>Informações sobre histórico de saúde, diagnósticos e prescrições.</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">3.3. Dados técnicos e de navegação</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Endereço IP, tipo de dispositivo, navegador, sistema operacional e cookies;</li>
                <li>Dados coletados automaticamente para garantir segurança e melhorar a experiência de uso.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. Finalidade do uso dos dados</h2>
              <p>Os dados coletados são utilizados para:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Criar e manter o repositório digital de pacientes;</li>
                <li>Permitir o uso das funcionalidades da plataforma de IA;</li>
                <li>Gerar análises, relatórios e prognósticos personalizados;</li>
                <li>Aprimorar continuamente os algoritmos e serviços oferecidos;</li>
                <li>Cumprir obrigações legais e regulatórias;</li>
                <li>Garantir segurança, auditoria e rastreabilidade dos acessos.</li>
              </ul>
              <p className="mt-4">A Elinia não utiliza dados clínicos para fins publicitários, comerciais ou não relacionados à finalidade médica.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. Base legal para o tratamento</h2>
              <p>O tratamento de dados realizado pela Elinia baseia-se nas seguintes hipóteses legais da LGPD:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Execução de contrato (uso da plataforma pelo profissional);</li>
                <li>Cumprimento de obrigação legal ou regulatória;</li>
                <li>Tutela da saúde, realizada por profissionais e serviços de saúde;</li>
                <li>Consentimento, quando aplicável (ex: envio de comunicações e atualizações).</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Armazenamento e segurança dos dados</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>Todos os dados são criptografados em repouso e em trânsito.</li>
                <li>A infraestrutura é hospedada em provedores seguros e certificados internacionalmente (como AWS ou Google Cloud).</li>
                <li>O acesso é restrito a usuários autenticados e autorizado apenas para finalidades específicas.</li>
                <li>Rotinas de backup e auditoria são aplicadas regularmente.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Compartilhamento de informações</h2>
              <p>A Elinia não compartilha dados pessoais ou clínicos com terceiros sem autorização do titular, exceto:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Quando houver exigência legal, judicial ou regulatória;</li>
                <li>Para parceiros técnicos responsáveis por infraestrutura e segurança (sob contrato de confidencialidade e conformidade com a LGPD);</li>
                <li>Com o próprio profissional de saúde responsável pelos dados do paciente.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. Direitos dos titulares</h2>
              <p>Em conformidade com a LGPD, o titular dos dados pode, a qualquer momento:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Acessar suas informações;</li>
                <li>Solicitar correção ou exclusão;</li>
                <li>Revogar consentimentos concedidos;</li>
                <li>Solicitar a portabilidade de seus dados;</li>
                <li>Obter informações sobre o compartilhamento e tratamento realizado.</li>
              </ul>
              <p className="mt-4">As solicitações podem ser feitas através do e-mail: privacidade@miasalute.com.br</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">9. Retenção dos dados</h2>
              <p>Os dados são armazenados pelo período necessário para o cumprimento das finalidades desta Política, respeitando prazos legais e regulatórios do setor de saúde. Após o encerramento da conta, os dados poderão ser anonimizados para fins estatísticos e de melhoria do serviço.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">10. Cookies e tecnologias de rastreamento</h2>
              <p>A Elinia utiliza cookies e tecnologias similares para:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Melhorar o desempenho da plataforma;</li>
                <li>Lembrar preferências de navegação;</li>
                <li>Coletar métricas anônimas de uso.</li>
              </ul>
              <p className="mt-4">O usuário pode desativar os cookies a qualquer momento no seu navegador, ciente de que isso pode afetar o funcionamento de algumas funcionalidades.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">11. Atualizações desta política</h2>
              <p>Esta Política de Privacidade poderá ser atualizada periodicamente para refletir mudanças legais, técnicas ou operacionais. Recomenda-se consultar esta página regularmente para estar ciente da versão mais recente.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">12. Contato</h2>
              <p>Dúvidas, solicitações ou reclamações sobre privacidade podem ser enviadas para:</p>
              <p className="mt-2">📧 contato@miasalute.com.br</p>
              <p>📍 Responsável pelo tratamento de dados: Wagner Cecconello (DPO) – Elinia</p>
            </section>
          </article>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-primary via-primary/95 to-secondary text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="space-y-4">
              <img src={logoFooter} alt="Elinia" className="mb-4" />
              <p className="text-sm opacity-90 leading-relaxed">
                Inteligência que facilita a vida de quem cuida.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-base">Navegação</h4>
              <div className="space-y-2 text-sm">
                <button onClick={() => { navigate("/sobre"); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="block hover:translate-x-1 transition-transform">→ Sobre</button>
                <button onClick={() => { navigate("/como-funciona"); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="block hover:translate-x-1 transition-transform">→ Como funciona</button>
                <button onClick={() => { navigate("/precos"); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="block hover:translate-x-1 transition-transform">→ Preços</button>
                <button onClick={() => {
                  navigate("/onboarding");
                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                }} className="block hover:translate-x-1 transition-transform">→ Cadastre-se</button>
                <button onClick={() => { navigate("/cases"); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="block hover:translate-x-1 transition-transform">→ Cases</button>
                <button onClick={() => { navigate("/blog"); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="block hover:translate-x-1 transition-transform">→ Blog</button>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-base">Legal</h4>
              <div className="space-y-2 text-sm">
                <button onClick={() => { navigate("/politica-privacidade"); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="block hover:translate-x-1 transition-transform">→ Política de Privacidade</button>
                <button onClick={() => { navigate("/termos-uso"); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="block hover:translate-x-1 transition-transform">→ Termos de Uso</button>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-base">Contato</h4>
              <div className="space-y-2 text-sm">
                <p className="flex items-center gap-2 opacity-90">
                  <Mail className="h-4 w-4" />
                  contato@miasalute.com.br
                </p>
                <button
                  onClick={() => {
                    navigate("/contato");
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-all hover:scale-105"
                >
                  Fale conosco
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 text-center text-sm opacity-90">
            <p>&copy; 2026 Elinia. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PoliticaPrivacidade;
